import { normalize, pluralize } from "../../../utils/typeUtils"
import { useFind } from "../../../state/reducers/hooks"
import { DataType, TableName } from "../../../tables"
import { Product, ProductType } from "../state/types"
import { Supplier, usePurchaseLoaders } from "../../purchases"
import { FieldType, useFiltersHooks } from "../../../features/ui"
import { DefaultUnitTypeId, useDataTypesHooks } from "../../../features/data-types"
import { UnitTypeCategory, useStockLoaders, useUnitTypeLoaders } from "../../stock"
import { getMeasureLabel } from "../utils/measuresUtils"
import { Measure, MeasureType } from "../../common"
import { useProductLoaders } from "../loaders/productLoaders"
import { useBudgetLoaders, useCustomerTypeLoaders, useHoardLoaders, useSaleLoaders } from "../../sales"
import { SettingsModule, useSettingsLoaders } from "../../administration"

export const useProductHooks = () => {
    const { search } = useFiltersHooks()
    const { formatValue } = useDataTypesHooks()
    const find = useFind()

    const productLoaders = useProductLoaders()
    const unitTypeLoaders = useUnitTypeLoaders()
    const stockLoaders = useStockLoaders()
    const purchaseLoaders = usePurchaseLoaders()
    const budgetLoaders = useBudgetLoaders()
    const saleLoaders = useSaleLoaders()
    const hoardLoaders = useHoardLoaders()
    const customerTypeLoaders = useCustomerTypeLoaders()
    const settingsLoaders = useSettingsLoaders()

    const describeMeasure = (product: Product) => {
        const isBulk = product.type === ProductType.BULK
        const isPackage = product.type === ProductType.PACKAGE
        const unitsLabel = !isBulk ? `${formatValue(product.units, FieldType.NUMBER)} ` : ''
        const { findUnitType } = unitTypeLoaders()
        const unitType = findUnitType(product.unitTypeId)!
        const unitTypeLabel = isPackage ? 'caja' : (isBulk ? unitType.name.toLowerCase() : unitType.abbreviation)
        return `${unitsLabel}${unitTypeLabel}`
    }

    const describeUnitType = (
        product: Product,
        abbreviate = true,
        plural = false
    ) => {
        const isUnitary = product.type === ProductType.UNITARY
        const { findUnitType } = unitTypeLoaders()
        const unitType = findUnitType(product.unitTypeId)!
        const unitaryLabel = abbreviate ? 'u' : plural ? 'unidades' : 'unidad'
        const bulkLabel = abbreviate ? unitType.abbreviation!.toLowerCase() : plural ? pluralize(unitType.name.toLowerCase()) : unitType.name.toLowerCase()
        return product.measureLabel ? getMeasureLabel(product.measureLabel, plural) : (isUnitary ? unitaryLabel : bulkLabel)
    }

    const describeQuantity = (
        quantity: number,
        product: Product
    ) => {
        const { type, unitTypeId, secondaryUnitTypeId } = product

        const isUnitary = type === ProductType.UNITARY
        const isPackage = type === ProductType.PACKAGE
        
        const unitsLabel = formatValue(quantity, FieldType.NUMBER)
        const { findUnitType } = unitTypeLoaders()
        const unitType = findUnitType(unitTypeId)!
        const unitTypeLabel = describeUnitType(product, true, quantity > 1)

        const secondaryUnits = isUnitary ? quantity * product.units! : (
            isPackage ? quantity * product.secondaryUnits! : undefined
        )
        const secondaryUnitsLabel = formatValue(secondaryUnits, FieldType.NUMBER)
        const secondaryUnitType = findUnitType(secondaryUnitTypeId)
        const secondaryUnitTypeLabel = isUnitary ? unitType.abbreviation : (
            isPackage ? secondaryUnitType?.abbreviation : undefined
        )

        const showSecondaryStock = quantity > 0 && ((isUnitary && (product.units !== 1 || unitTypeId !== DefaultUnitTypeId)) || isPackage)
        const secondaryStock = showSecondaryStock ? ` / ${secondaryUnitsLabel} ${secondaryUnitTypeLabel}` : ''

        return `${unitsLabel} ${unitTypeLabel}${secondaryStock}`
    }

    const getWeight = (product: Product): Measure => {
        const { findUnitType } = unitTypeLoaders()
        const unitType = findUnitType(product.unitTypeId)!
        if (unitType.category === UnitTypeCategory.WEIGHT) {
            return {
                type: MeasureType.WEIGHT,
                units: product.units || 1,
                unitTypeId: unitType.id
            }
        } else {
            return product.measures.find(measure => measure.type === MeasureType.WEIGHT)!
        }
    }

    const canRemoveProduct = async (productId: string) => {
        const { loadProductsWithProp } = productLoaders()
        const productsWithEquivalences = await loadProductsWithProp('equivalences', true)
        const products = productsWithEquivalences.filter(product => product.equivalences?.some(equivalence => equivalence.productId === productId))
        
        const { loadProductStocks } = stockLoaders()
        const productStocks = await loadProductStocks(productId, undefined, undefined, true)
        const stocksUnits = productStocks.reduce((acc, stock) => acc + stock.availableUnits + stock.reservedUnits, 0)

        const { loadAllPurchases } = purchaseLoaders()
        const allPurchases = await loadAllPurchases()
        const purchases = allPurchases.filter(purchase => purchase.items.some(item => item.productId === productId))
        
        const { loadAllBudgets } = budgetLoaders()
        const allBudgets = await loadAllBudgets()
        const budgets = allBudgets.filter(budget => budget.items.some(item => item.productId === productId))
        
        const { loadAllSales } = saleLoaders()
        const allSales = await loadAllSales()
        const sales = allSales.filter(sale => sale.items.some(item => item.productId === productId))

        const { loadAllHoards } = hoardLoaders()
        const allHoards = await loadAllHoards()
        const hoards = allHoards.filter(hoard => hoard.items.some(item => item.productId === productId))
        
        const { loadCustomerTypesWithProp } = customerTypeLoaders()
        const customerTypesWithProductYields = await loadCustomerTypesWithProp('productYields', true)
        const customerTypes = customerTypesWithProductYields.filter(customerType => customerType.productYields?.some(productYield => productYield.productId === productId))
        
        const { loadModuleSettings } = settingsLoaders()
        const saleSettings = await loadModuleSettings(SettingsModule.SALES)
        const inSaleSettings = ((saleSettings.hoardIgnoredProductIds || []) as string[]).includes(productId)
        const purchaseSettings = await loadModuleSettings(SettingsModule.PURCHASES)
        const inPurchaseSettings = ((purchaseSettings.linkedProductIds || []) as string[]).includes(productId)

        return products.length === 0 &&
            stocksUnits === 0 &&
            purchases.length === 0 &&
            budgets.length === 0 &&
            sales.length === 0 &&
            hoards.length === 0 &&
            customerTypes.length === 0 &&
            !inSaleSettings &&
            !inPurchaseSettings
    }

    const searchProducts = (
        products: Product[],
        text = '',
        showAll = false
    ): Product[] => {
        const valuesFn = (productDT: DataType) => {
            const { code, supplierCode, name, supplierId } = productDT as Product
            const supplier = find(TableName.SUPPLIERS, supplierId)! as Supplier
            return [
                code.toString(),
                supplierCode ? normalize(supplierCode) : '',
                normalize(name),
                normalize(supplier.name)
            ]
        }
        const sortFn = (productDT_A: DataType, productDT_B: DataType) => 
            (productDT_A as Product).name.localeCompare((productDT_B as Product).name)

        return search(products, valuesFn, sortFn, text, showAll) as Product[]
    }

    return {
        describeMeasure,
        describeUnitType,
        describeQuantity,
        getWeight,
        canRemoveProduct,
        searchProducts
    }
}

export const productHelpers = {
    describeProductCode: (product: Product) => {
        return `#${product.code}`
    }
}
